import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useRef, useState } from 'react';

import {
  getWalletSyncStatusOptions,
  PORTFOLIO_QUERY_KEYS,
  type WalletSyncStatus,
} from '@/api/portfolio/queries';
import { invalidateTransactionViews } from '@/api/transaction-views/cache-invalidation';
import type { WalletSyncResponse } from '@/api/wallet/queries';

import {
  getActiveWalletSyncRuns,
  getTerminalWalletSyncRunIds,
  mergeWalletSyncRuns,
  type TrackedWalletSyncRun,
} from './wallet-portfolio-sync';

export const useWalletSyncRunTracker = ({ enabled }: { enabled: boolean }) => {
  const queryClient = useQueryClient();
  const pendingRuns = useRef<readonly TrackedWalletSyncRun[]>([]);
  const manualRunsRef = useRef<readonly TrackedWalletSyncRun[]>([]);
  const [manualRuns, setManualRuns] = useState<readonly TrackedWalletSyncRun[]>([]);
  const { data: walletSyncStatus } = useQuery({
    ...getWalletSyncStatusOptions(),
    enabled,
  });

  const registerActiveRuns = useCallback((response: WalletSyncResponse, isManual: boolean) => {
    const activeRuns = getActiveWalletSyncRuns(response);
    if (activeRuns.length === 0) return;

    pendingRuns.current = mergeWalletSyncRuns(pendingRuns.current, activeRuns);
    if (!isManual) return;

    const nextManualRuns = mergeWalletSyncRuns(manualRunsRef.current, activeRuns);
    manualRunsRef.current = nextManualRuns;
    setManualRuns(nextManualRuns);
  }, []);

  const reconcileCompletedRuns = useCallback(
    (status: WalletSyncStatus | undefined) => {
      if (!status || pendingRuns.current.length === 0) return;

      const terminalRunIds = getTerminalWalletSyncRunIds(status, pendingRuns.current);
      if (terminalRunIds.length === 0) return;

      const terminalRunIdSet = new Set(terminalRunIds);
      pendingRuns.current = pendingRuns.current.filter(({ runId }) => !terminalRunIdSet.has(runId));
      const nextManualRuns = manualRunsRef.current.filter(
        ({ runId }) => !terminalRunIdSet.has(runId),
      );
      manualRunsRef.current = nextManualRuns;
      setManualRuns(nextManualRuns);
      void invalidateTransactionViews(queryClient);
    },
    [queryClient],
  );

  useEffect(() => {
    const unsubscribe = queryClient.getQueryCache().subscribe(() => {
      reconcileCompletedRuns(
        queryClient.getQueryData<WalletSyncStatus>([PORTFOLIO_QUERY_KEYS.walletSyncStatus]),
      );
    });
    return unsubscribe;
  }, [queryClient, reconcileCompletedRuns]);

  useEffect(() => {
    reconcileCompletedRuns(walletSyncStatus);
  }, [reconcileCompletedRuns, walletSyncStatus]);

  return {
    registerActiveRuns,
    hasManualRuns: manualRuns.length > 0,
    walletSyncStatus,
  };
};
